// src/components/common/DeleteModal/DeleteModal.jsx
import React, { useEffect, useRef } from 'react';
import { FiTrash2, FiX } from 'react-icons/fi';
import styles from './DeleteModal.module.css';

/**
 * DeleteModal - Specialized confirmation dialog for delete operations
 * Provides visual feedback with danger styling and clear messaging
 */
const DeleteModal = ({
    isOpen,
    title = 'Confirmar Exclusão',
    message = 'Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.',
    itemName = '',
    itemCode = '',
    confirmText = 'Excluir',
    cancelText = 'Cancelar',
    onConfirm,
    onCancel,
    isLoading = false,
    icon = null,
    children = null,
}) => {
    const confirmButtonRef = useRef(null);

    // Auto-focus on confirm button when opened
    useEffect(() => {
        if (isOpen && confirmButtonRef.current) {
            setTimeout(() => confirmButtonRef.current.focus(), 50);
        }
    }, [isOpen]);

    // ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !isLoading) {
                onCancel?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isLoading, onCancel]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className={styles.backdrop}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isLoading) {
                    onCancel?.();
                }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
        >
            <div
                className={styles.dialog}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.header}>
                    <div className={styles.iconContainer}>
                        {icon || <FiTrash2 size={24} className={styles.dangerIcon} />}
                    </div>
                    <h3 id="delete-dialog-title" className={styles.title}>
                        {title}
                    </h3>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onCancel}
                        disabled={isLoading}
                        aria-label="Fechar"
                    >
                        <FiX size={20} />
                    </button>
                </div>

                <div className={styles.content}>
                    <p className={styles.message}>{message}</p>

                    {(itemName || itemCode) && (
                        <div className={styles.itemInfo}>
                            <span className={styles.itemLabel}>Item:</span>
                            <span className={styles.itemValue}>
                                {itemCode && <span className={styles.itemCode}>{itemCode}</span>}
                                {itemName && <span className={styles.itemName}>{itemName}</span>}
                            </span>
                        </div>
                    )}

                    {children && <div className={styles.children}>{children}</div>}
                </div>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        ref={confirmButtonRef}
                        className={styles.deleteButton}
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner} />
                                Processando...
                            </>
                        ) : (
                            <>
                                <FiTrash2 size={16} />
                                {confirmText}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteModal;